"use client";

import React from "react";
import { Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";

// 獨立出來的表單內層元件
function ContactFormInner({
  isSubmitting,
  isSubmitted,
  submitError,
  successMessage,
  handleSubmit,
  resetForm,
  control,
  isValid
}: any) {
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-16 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <h3 className="text-xl font-medium text-[#664d46]">預約成功</h3>
        <p className="text-sm text-[#897874] max-w-xs">{successMessage}</p>
        <Button type="button" onClick={resetForm} className="mt-6 rounded-none bg-[#664d46] text-white hover:bg-[#523d38] text-xs tracking-widest px-6">
          再次填寫
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6 md:space-y-7">
      <div className="text-left space-y-1 mb-2 font-sans font-normal text-[#664d46] text-base tracking-wide leading-relaxed">
        <p>填寫簡單資訊，</p>
        <p>我們會與您聯繫確認時間</p>
      </div>

      {/* 姓名輸入 */}
      <FormField control={control} name="name" rules={{
      required: "請輸入您的稱呼"
    }} render={({
      field
    }) => <FormItem className="space-y-2 text-left">
            <FormLabel className="font-sans font-light text-[#664d46] text-sm tracking-wider">怎麼稱呼您？</FormLabel>
            <FormControl>
              <Input placeholder="例:王小姐" {...field} className="bg-transparent text-[#664d46] border-[#d3c2b5] rounded-none focus-visible:ring-0 focus-visible:border-[#664d46] placeholder:text-[#b0a49c]/60 h-11 px-3 text-sm" />
            </FormControl>
            <FormMessage className="text-xs text-red-500" />
          </FormItem>} />

      {/* 聯絡電話（升級 10 碼手機格式檢查） */}
      <FormField control={control} name="phone" rules={{
      required: "請輸入您的手機號碼",
      pattern: {
        value: /^09\d{8}$/,
        // 🎯 限制必須是 09 開頭且後面接 8 碼純數字，總共 10 碼
        message: "請輸入正確的手機號碼格式（10位純數字，例:0912345678）"
      }
    }} render={({
      field
    }) => <FormItem className="space-y-2 text-left">
            <FormLabel className="font-sans font-light text-[#664d46] text-sm tracking-wider">聯絡方式</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="手機號碼（10碼數字）" {...field} className="bg-transparent text-[#664d46] border-[#d3c2b5] rounded-none focus-visible:ring-0 focus-visible:border-[#664d46] placeholder:text-[#b0a49c]/50 h-11 px-3 text-sm" />
            </FormControl>
            <FormMessage className="text-xs text-red-500" />
          </FormItem>} />

      {/* 需求描述（升級為必填欄位） */}
      <FormField control={control} name="message" rules={{
      required: "請簡單描述您的需求或空間狀況"
    }} // 🎯 強制不能為空
    render={({
      field
    }) => <FormItem className="space-y-2 text-left">
            <FormLabel className="font-sans font-light text-[#664d46] text-sm tracking-wider">想了解的需求或空間狀況</FormLabel>
            <FormControl>
              <Textarea placeholder="例:想了解淺色木地板、希望耐磨好清潔、客廳空間約20坪..." className="min-h-[100px] resize-none bg-transparent text-[#664d46] border-[#d3c2b5] rounded-none focus-visible:ring-0 focus-visible:border-[#664d46] placeholder:text-[#b0a49c]/50 p-3 text-sm leading-relaxed" {...field} />
            </FormControl>
            <FormMessage className="text-xs text-red-500" />
          </FormItem>} />

      {submitError && <div className="p-3 bg-red-50 border border-red-100 text-left">
          <p className="text-xs text-red-600">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting || !isValid} className="w-full h-12 text-sm rounded-none bg-[#664d46] text-white hover:bg-[#523d37] transition-all duration-300 font-sans font-normal tracking-widest border-none shadow-none mt-4 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-[#d3c2b5] disabled:text-[#664d46]/60">
        {isSubmitting ? <div className="flex items-center gap-2">
            <Loader className="animate-spin size-4" />
            <span>處理中...</span>
          </div> : "預約看看"}
      </Button>
    </Form>;
}
export default function ContactUs() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const formMethods = useForm({
    mode: "onChange",
    // 🚀 每次使用者打字時，即時進行 10 碼 Regex 與必填驗證
    defaultValues: {
      name: "",
      phone: "",
      message: ""
    }
  });
  const {
    formState: {
      isValid
    }
  } = formMethods;
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const successMessage = "感謝您的預約！專員將儘速與您聯繫。";
  const onSubmit = async (data: any) => {
    setIsSubmitting(true);
    setSubmitError(null);
    const GOOGLE_FORM_ACTION_URL = "https://docs.google.com/forms/d/e/1FAIpQLSeo3NExarnfQ2ZHQE3kFY2scEyO9UEfjswQ3L7326bMmRgUEQ/formResponse";
    try {
      const formData = new FormData();
      formData.append("entry.1415399383", data.name || "");
      formData.append("entry.1820778637", data.phone || "");
      formData.append("entry.1766674335", data.message || "");
      await fetch(GOOGLE_FORM_ACTION_URL, {
        method: "POST",
        mode: "no-cors",
        body: formData
      });
      setIsSubmitted(true);
    } catch (error) {
      console.error("表單直連發送錯誤:", error);
      setSubmitError("網路連線異常，請稍後重試。");
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleReset = () => {
    formMethods.reset();
    setIsSubmitted(false);
    setSubmitError(null);
  };
  return <section id="contact-form" className="relative w-full py-20 md:py-28 bg-[#f4ebe1] overflow-hidden select-none scroll-mt-24">
      <div className="absolute inset-0 z-0 w-full h-full overflow-hidden bg-[#f4ebe1]">
        <img src="https://hent.services/wp-content/uploads/2026/05/Contact_Bottom_BG.webp" alt="Showroom Contact Background" className="w-full h-full object-cover object-bottom pointer-events-none" />
        <div className="absolute inset-0 bg-[#f4ebe1]/10 pointer-events-none" />
      </div>

      <div ref={sectionRef} className={cn("max-w-6xl mx-auto px-6 lg:px-8 relative z-10 transition-all duration-[1200ms] ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          <div className="lg:col-span-5 flex flex-col justify-start text-left space-y-6 pt-0 lg:pt-4">
            <div className="space-y-3 font-sans font-medium text-[#897874] text-2xl sm:text-3xl md:text-4xl tracking-widest leading-snug">
              <h2>來門市走走</h2>
              <h2>感受地板的真實樣貌</h2>
            </div>
            
            <div className="w-10 h-[2px] bg-[#d3c2b5] my-1" />
            
            <p className="font-sans font-light text-[#a5948e] text-sm sm:text-base leading-relaxed tracking-widest">
              每個空間都有自己的光線與溫度
              <br />
              我們會陪你找到最剛好的那一種
            </p>
          </div>

          {/* 右側表單區 */}
          <div className="lg:col-span-7 w-full flex justify-center lg:justify-end">
            <div className="w-full max-w-lg bg-white p-8 sm:p-10 md:p-12 shadow-[30px_30px_60px_rgba(86,73,63,0.04)] rounded-sm">
              <ContactFormInner isSubmitting={isSubmitting} isSubmitted={isSubmitted} submitError={submitError} successMessage={successMessage} handleSubmit={formMethods.handleSubmit(onSubmit)} resetForm={handleReset} control={formMethods.control} isValid={isValid} />
            </div>
          </div>

        </div>
      </div>
    </section>;
}